/**
 * Talent-drawer vocabularies — the closed value sets the business-web Vetting
 * and Sponsored drawers turn into i18n keys. (F29)
 *
 * Every drawer badge, chip and label that shows a server value used to build
 * its key at runtime — `` t(`care.phase.${phase}`) `` — so nothing could tell
 * whether a key existed for every value the server sends. A value with no key
 * renders its own raw name on screen, in every locale, and is caught by nobody.
 *
 * Each vocabulary is declared here as an `as const` ARRAY so it is readable at
 * RUNTIME as well as by the compiler. Two consumers depend on that:
 *
 *   • the drawer components, whose value → key maps are
 *     `satisfies Record<Vocabulary, string>` — a new value is a TYPECHECK
 *     failure in business-web, not a screen failure;
 *   • `packages/locale/src/dynamic-key-coverage.spec.ts`, which iterates these
 *     arrays and fails while `en.json` lacks a key for any member.
 *
 * WHERE EACH LIST COMES FROM — read before adding a value. A list is one of:
 *
 *   DERIVED   compiled against a union this package already owns, through
 *             {@link exhaustiveList}: it cannot omit or invent a member.
 *   OWNED     the drawer's own vocabulary; the app-local type is derived FROM
 *             this array, so the array is the single declaration.
 *   MIRROR    the value set's source of truth lives where `@tonnie/types`
 *             cannot import it (a Drizzle `$type`, an SDK inline union, a
 *             platform-api or brain-service constant). The array is a
 *             hand-maintained copy; the drawer map indexes it with the
 *             server-typed value, so a union that GROWS on the typed side fails
 *             typecheck in business-web — but a varchar that starts carrying a
 *             new value does not. Each mirror names its source.
 */

import type { ICompanyDiagnosis, IPitchVideoAnalysis } from '../heroes/brain';
import type { CheckupPillarCode } from '../heroes/brain-mesh';
import type { OwnershipMode } from '../heroes/teleportal';
import type { LifecycleStepId } from '../pipeline-lifecycle';

/**
 * Returns `values` unchanged, but only compiles when the array lists EVERY
 * member of `U` and nothing else: `readonly U[]` rejects a stranger, and the
 * `[U] extends [A[number]]` clause rejects an omission.
 */
function exhaustiveList<U extends string>() {
  return <const A extends readonly U[]>(
    values: A & ([U] extends [A[number]] ? unknown : never)
  ): A => values;
}

// ─── Sponsored drawer ────────────────────────────────────────────────────────

/**
 * OWNED. The drawer heroes whose footer "Send nudge" fires a messaging-hub
 * template (`smartNudge.cta.*` / `smartNudge.sent.*`). Evidence and the
 * hero-less drawer render a neutral Close instead.
 */
export const SMART_NUDGE_HEROES = ['mobility', 'adaptation', 'care'] as const;
export type SmartNudgeHero = (typeof SMART_NUDGE_HEROES)[number];

// ─── Vetting drawer ──────────────────────────────────────────────────────────

/**
 * DERIVED from `ICompanyDiagnosis['overallSignal']` — Brain's GREEN/AMBER/RED
 * recommendation behind the header tone ribbon (`toneRibbon.tone.*`). The
 * ribbon's fourth tone, NEUTRAL, is not a Brain value and has its own literal
 * keys.
 */
export const TONE_RIBBON_SIGNALS = exhaustiveList<ICompanyDiagnosis['overallSignal']>()([
  'GREEN',
  'AMBER',
  'RED',
]);
export type ToneRibbonSignal = (typeof TONE_RIBBON_SIGNALS)[number];

/**
 * OWNED. The five vetting phase stops, in order (`toneRibbon.phase.*`,
 * `phaseStrip.vetting.*`).
 */
export const VETTING_PHASE_STOPS = [
  'invited',
  'started',
  'submitted',
  'scored',
  'verdict',
] as const;
export type VettingPhaseStop = (typeof VETTING_PHASE_STOPS)[number];

/**
 * Where a lifecycle step sits on the Vetting drawer's phase strip — for BOTH
 * tracks (CheckUp and TestIt) and through the company's verdict. (P1.8)
 *
 * The drawer placed the strip from the CheckUp's `vettingState` alone, so a
 * TestIt talent with every test done and a "Declined" verdict still read
 * "Started" with a "waiting for the CheckUp" banner and a Send Reminder footer;
 * and `verdict` was a stop nothing could reach. The server answers it per row
 * from the step. `null` for a row that has left the pipeline (archived, the
 * citizen left): it sits on no stop.
 */
export function vettingPhaseOfStep(step: LifecycleStepId): VettingPhaseStop | null {
  switch (step) {
    case 'not_in_pipeline':
    case 'not_invited':
    case 'invitation_draft':
    case 'invitation_sent':
    case 'invitation_delivered':
    case 'invitation_opened':
    case 'invitation_landed':
    case 'invitation_signed_up_not_accepted':
    case 'invitation_bounced':
    case 'invitation_expired':
    case 'invitation_revoked':
    case 'invitation_declined':
    case 'onboarding_incomplete':
    case 'checkup_not_requested':
    case 'waiting_for_checkup':
    case 'testit_waiting':
      return 'invited';
    case 'checkup_in_progress':
    case 'testit_in_progress':
      return 'started';
    case 'checkup_awaiting_score':
      return 'submitted';
    // Scored — a process fact the company sees whether or not the citizen
    // shares the result (D4). For the two not-shared steps the strip stops
    // here; the ribbon says why the score is not on screen.
    case 'awaiting_review':
    case 'checkup_withheld':
    case 'consent_withdrawn':
    case 'testit_complete':
      return 'scored';
    case 'testit_passed':
    case 'testit_declined':
    case 'sponsorship_offered': // the company decided and offered; the talent answers next
    case 'sponsored_pending':
    case 'sponsored_mobility':
    case 'sponsored_adaptation':
    case 'sponsored_care':
    case 'sponsored_evidence':
      return 'verdict';
    case 'archived':
    case 'citizen_left':
      return null;
    default: {
      const unreachable: never = step;
      return unreachable;
    }
  }
}

/**
 * OWNED. The four sponsored-journey steps of the Sponsored drawer's phase strip
 * (`phaseStrip.sponsored.*`).
 */
export const SPONSORED_JOURNEY_STEPS = ['offered', 'accepted', 'sponsored', 'thriving'] as const;
export type SponsoredJourneyStep = (typeof SPONSORED_JOURNEY_STEPS)[number];

/**
 * OWNED. The two tiers the Top-risks card draws from — Brain's
 * `culturalArchetypes.highRisk` / `.moderateRisk` buckets
 * (`topRisks.severityShort.*` / `topRisks.a11yLabel.*`).
 */
export const TOP_RISK_SEVERITIES = ['high', 'moderate'] as const;
export type TopRiskSeverity = (typeof TOP_RISK_SEVERITIES)[number];

/** DERIVED from `IPitchVideoAnalysis['sentiment']` (`pitch.sentimentValue.*`). */
export const PITCH_SENTIMENTS = exhaustiveList<NonNullable<IPitchVideoAnalysis['sentiment']>>()([
  'positive',
  'neutral',
  'negative',
]);
export type PitchSentiment = (typeof PITCH_SENTIMENTS)[number];

/** DERIVED from `IPitchVideoAnalysis['deliveryMetrics']` (`pitch.deliveryMetric.*`). */
export const PITCH_DELIVERY_METRICS = exhaustiveList<
  keyof NonNullable<IPitchVideoAnalysis['deliveryMetrics']>
>()([
  'communicationClarity',
  'confidenceLevel',
  'energyLevel',
  'pacing',
  'fillerWords',
  'articulation',
]);
export type PitchDeliveryMetric = (typeof PITCH_DELIVERY_METRICS)[number];

/**
 * DERIVED from `CHECKUP_PILLAR_MESH_GROUP` (`heroes/brain-mesh.ts`) — the
 * pillar acronyms Brain emits (`pillars.fullName.*`), the same declaration
 * Brain tags its pillars from.
 */
export const CHECKUP_PILLAR_CODES = exhaustiveList<CheckupPillarCode>()([
  'ICM',
  'MA',
  'WMF',
  'CP',
  'CP_ENGLISH',
  'CP_NATIVE_TARGET',
  'GPE',
  'AR',
  'PRM',
]);

/**
 * OWNED — and shared with the server, which is what makes it a contract rather
 * than a mirror. The band a pillar scoring anchor carries
 * (`radar.tooltip.pillar.band.*`), decided by platform-api from the anchor's
 * position (`shapePillarCatalog`).
 *
 * The first four positions have a name. A fifth or later anchor is `tier`, and
 * its 1-based position travels beside it as a VALUE. It used to travel as the
 * English sentence `'Tier 5'`, built on the server — which the drawer could
 * only render raw, in every locale.
 */
export const PILLAR_BAND_TAGS = ['excellent', 'solid', 'mixed', 'weak', 'tier'] as const;
export type PillarBandTag = (typeof PILLAR_BAND_TAGS)[number];

/** The named bands, in anchor order (Brain ships anchors top-down). */
export const PILLAR_NAMED_BANDS = [
  'excellent',
  'solid',
  'mixed',
  'weak',
] as const satisfies readonly PillarBandTag[];

/**
 * What a scoring anchor says about its band: a code, and its position for
 * `tier`. The field is `code` because the server's translate-on-read skips
 * `code` leaves — a bare lowercase `excellent` under any other name would be
 * translated and stop matching this vocabulary.
 */
export interface PillarAnchorBand {
  code: PillarBandTag;
  /** 1-based anchor position — the `{{tier}}` value of the `tier` label. */
  tier: number;
}

/** OWNED. The CRA severity tiers of the Insights tab (`cra.severityTier.*`). */
export const CRA_SEVERITY_TIERS = ['low', 'moderate', 'high'] as const;
export type CraSeverityTier = (typeof CRA_SEVERITY_TIERS)[number];

/** OWNED. The qualitative confidence bands (`verdict.confidenceBand.*`). */
export const CONFIDENCE_BANDS = ['high', 'moderate', 'low'] as const;
export type ConfidenceBand = (typeof CONFIDENCE_BANDS)[number];

/**
 * DERIVED from the CheckUp's MESH foundations (M / E / S / H), in the order
 * the verdict breaks ties. The server names the foundation the verdict leads
 * with (`insights.verdict`); the drawers label it.
 */
export const MESH_FOUNDATION_KEYS = ['motivation', 'english', 'softCultural', 'hardOther'] as const;
export type MeshFoundationKey = (typeof MESH_FOUNDATION_KEYS)[number];

/**
 * The CheckUp's server-side reading for the drawers' verdict: how sure the
 * engine is about the pillars (average of what Brain reported, `null` when it
 * reported none — never a default), and which foundation carries the profile
 * and which, if any, is the actionable weakness.
 */
export interface CheckupInsights {
  /** Average Brain pillar confidence, 0–100; `null` when no pillar carries one. */
  pillarConfidence: number | null;
  pillarConfidenceBand: ConfidenceBand | null;
  /** `null` before any foundation is scored. */
  verdict: { strongest: MeshFoundationKey; weakness: MeshFoundationKey | null } | null;
}

// ─── Hero phase cards (both drawers) ─────────────────────────────────────────

/** OWNED. The heroes a drawer phase card can stand for (`phase.<hero>.*`). */
export const TALENT_DRAWER_HEROES = [
  'checkup',
  'mobility',
  'adaptation',
  'care',
  'evidence',
] as const;
export type TalentDrawerHero = (typeof TALENT_DRAWER_HEROES)[number];

/**
 * OWNED. A hero's engagement phase. `in_progress` renders the hero's own
 * content, so only `locked` and `awaiting` have phase-card copy
 * (`phase.<hero>.<phase>.title` / `.description`).
 */
export const TALENT_DRAWER_HERO_PHASES = ['locked', 'awaiting', 'in_progress'] as const;
export type TalentDrawerHeroPhase = (typeof TALENT_DRAWER_HERO_PHASES)[number];

// ─── Adaptation (QultureUp) ──────────────────────────────────────────────────

/**
 * MIRROR of the assessment level (`qultureup_assessments.level`, a varchar
 * documented `beginner | intermediate | advanced`) and of mission
 * `difficulty` in the brain-service QultureUp catalog
 * (`qultureup.difficulty<Level>`).
 */
export const QULTUREUP_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export type QultureUpLevel = (typeof QULTUREUP_LEVELS)[number];

/**
 * MIRROR of platform-api `rollupAdaptationWorlds` → `statusFromProgress`
 * (`modules/business/talents/talent-adaptation.rollup.ts`) — the per-world
 * status the admin Adaptation drawer shows (`adaptation.status.*`).
 */
export const ADAPTATION_WORLD_STATUSES = [
  'not_started',
  'in_progress',
  'completed',
  'locked',
] as const;
export type AdaptationWorldStatus = (typeof ADAPTATION_WORLD_STATUSES)[number];

// ─── Care ────────────────────────────────────────────────────────────────────

/**
 * MIRROR of the SDK `TalentCareDetail['overall']['phase']` inline union
 * (`care.phase.*`, whose keys are the camelCase form of these values).
 */
export const CARE_PHASES = ['pre_move', 'settling_in', 'adapting', 'thriving'] as const;
export type CarePhase = (typeof CARE_PHASES)[number];

/** Every Care phase's name — the ONE map web and phone name a phase through. */
export const CARE_PHASE_LABEL_KEY = {
  pre_move: 'care.phase.preMove',
  settling_in: 'care.phase.settlingIn',
  adapting: 'care.phase.adapting',
  thriving: 'care.phase.thriving',
} as const satisfies Record<CarePhase, string>;

export const isCarePhase = (value: string): value is CarePhase =>
  (CARE_PHASES as readonly string[]).includes(value);

/**
 * MIRROR of the SDK `checkinsSummary.moodTrend` inline union
 * (`care.checkins.trend.*`).
 */
export const CARE_MOOD_TRENDS = ['up', 'down', 'stable'] as const;
export type CareMoodTrend = (typeof CARE_MOOD_TRENDS)[number];

/**
 * MIRROR of `CareCheckpointStatus`, the `$type` of `care_checkpoints.status` in
 * `@tonnie/database` (`schema/heroes-care.ts`) — `care.checkpoints.status.*`.
 * NOT `CheckpointStatus` in `heroes/care.ts`, which lists `pending` / `missed`
 * that the column never holds and omits `due` / `overdue` / `skipped`, which
 * it does.
 */
export const CARE_CHECKPOINT_STATUSES = [
  'scheduled',
  'due',
  'overdue',
  'in_progress',
  'completed',
  'skipped',
  'cancelled',
] as const;
export type CareCheckpointStatus = (typeof CARE_CHECKPOINT_STATUSES)[number];

/**
 * MIRROR of `CommitmentTrend` in `@tonnie/heroes` (`care/commitment.ts`), which
 * exports it as a type only (`care.commitment.trend.*`).
 */
export const CARE_COMMITMENT_TRENDS = ['improving', 'stable', 'declining'] as const;
export type CareCommitmentTrend = (typeof CARE_COMMITMENT_TRENDS)[number];

/**
 * MIRROR of business-web `InterventionDisplayStatus`
 * (`features/business/shared/intervention-status.ts`) — the display state
 * `mapInterventionStatus` folds every stored interaction status into
 * (`carePage.routineStatus.*`).
 */
export const CARE_INTERVENTION_DISPLAY_STATUSES = [
  'open',
  'in_progress',
  'resolved',
  'cancelled',
] as const;
export type CareInterventionDisplayStatus = (typeof CARE_INTERVENTION_DISPLAY_STATUSES)[number];

// ─── Mobility (TelePortal) ───────────────────────────────────────────────────

// ── L5 region: TaskStatus reconciled with the database ──
/**
 * Every value `teleportal_tasks.status` holds — THE declaration. `@tonnie/database`
 * types the column with it (`schema/heroes-teleportal.ts`), `TaskStatus` in
 * `heroes/teleportal.ts` is this same union, and `mobility.taskStatus.*` is
 * keyed by it. Pinned against the column by `teleportal-task-status.spec.ts`
 * in `@tonnie/database`.
 */
// ── end L5 region ──
export const TELEPORTAL_TASK_STATUSES = [
  'pending',
  'in_progress',
  'completed',
  'blocked',
  'skipped',
  'archived',
] as const;
export type TeleportalTaskStatus = (typeof TELEPORTAL_TASK_STATUSES)[number];

/**
 * The `OwnershipMode` values (`teleportal_tasks.ownership_mode`) the Mobility
 * tasks list chips (`mobility.tasks.ownership.*`) — the tasks a company or a
 * government office acts on. `talent` is the default and `dual` is shared with
 * the talent; neither carries a chip.
 */
export const TELEPORTAL_CHIPPED_OWNERSHIP_MODES = [
  'company',
  'government',
] as const satisfies readonly OwnershipMode[];
export type TeleportalChippedOwnershipMode = (typeof TELEPORTAL_CHIPPED_OWNERSHIP_MODES)[number];

/**
 * MIRROR of `teleportal_tasks.location`, a varchar documented
 * `home_country | target_country | either | online` in `@tonnie/database`
 * (`mobility.tasks.location.*`).
 */
export const TELEPORTAL_TASK_LOCATIONS = [
  'home_country',
  'target_country',
  'either',
  'online',
] as const;
export type TeleportalTaskLocation = (typeof TELEPORTAL_TASK_LOCATIONS)[number];
